import { BaseModel, fillOwnProperties, registerModel } from '~/shared/model'
import {
  ACCOUNT_RESUME_PUBLISHING_STATUS,
  ACCOUNT_RESUME_STATUS,
  type AccountResumePublishingStatus,
  type AccountResumeStatusIds,
} from '~/shared/const/pages/account-resumes'
import { isUnknownObject, isString } from '~/shared/utils/guards'
import { toApiDateTimeFormatHour } from '~/shared/utils/date'
import { toCroppedImage } from '~/shared/utils/image'

interface PositionItem {
  desired_position_id: number
  desired_position: string
}

export default class AccountResume extends BaseModel {
  static override constructorName = 'AccountResumeModel'

  id: number = 0
  'customer_id': number = 0
  'first_name': string = ''
  'middle_name': string = ''
  'last_name': string = ''
  'birth_date': string = ''
  photo: string = ''
  phone: string = ''
  email: string = ''
  position: string = ''
  'experience_beauty': string = ''
  residence: string = ''
  description: string = ''
  salary: number = 0
  'employment_type': string = ''
  'update_date': string = ''
  'desired_position': PositionItem[] = []
  'desired_position_beauty': string = ''
  'publishing_status': string = ''
  'publishing_refuse_reason': string = ''
  'publishing_refuse_reason_description': string = ''
  'publishing_statuses': string[] = []
  status: string = ''
  'status_id': string = ''

  constructor(data?: unknown) {
    super(data)
    if (isUnknownObject(data)) {
      fillOwnProperties(this, data)

      if (isString(data.update_date)) {
        this.update_date = toApiDateTimeFormatHour(data.update_date)
      }

      if (Array.isArray(data.desired_position) && data.desired_position.length > 0) {
        this.desired_position_beauty = data.desired_position[0].desired_position
      }

      this.photo = toCroppedImage(data.photo, { width: 200, height: 200 })
    }
  }

  get isApproved(): boolean {
    return this.hasPublishingStatus(ACCOUNT_RESUME_PUBLISHING_STATUS.APPROVED)
  }

  get isRejected(): boolean {
    return this.hasPublishingStatus(ACCOUNT_RESUME_PUBLISHING_STATUS.REJECTED)
  }

  get isOnReview(): boolean {
    return this.hasPublishingStatus(ACCOUNT_RESUME_PUBLISHING_STATUS.ON_REVIEW)
  }

  get isPublishingHidden(): boolean {
    return this.hasPublishingStatus(ACCOUNT_RESUME_PUBLISHING_STATUS.HIDDEN)
  }

  get isDraft(): boolean {
    return this.hasPublishingStatus(ACCOUNT_RESUME_PUBLISHING_STATUS.DRAFT)
  }

  get isVisible(): boolean {
    return this.hasStatus(ACCOUNT_RESUME_STATUS.VISIBLE)
  }

  get isHidden(): boolean {
    return this.hasStatus(ACCOUNT_RESUME_STATUS.HIDDEN)
  }

  get isStatusDraft(): boolean {
    return this.hasStatus(ACCOUNT_RESUME_STATUS.DRAFT)
  }

  get isPendingModerate(): boolean {
    return this.hasStatus(ACCOUNT_RESUME_STATUS.PENDING_MODERATE)
  }

  get isSuspicious(): boolean {
    return this.hasStatus(ACCOUNT_RESUME_STATUS.SUSPICIOUS)
  }

  get isBlocked(): boolean {
    return this.hasStatus(ACCOUNT_RESUME_STATUS.BLOCKED)
  }

  private hasPublishingStatus(status: AccountResumePublishingStatus): boolean {
    return this.publishing_status === status
  }

  private hasStatus(ids: AccountResumeStatusIds): boolean {
    return ids.includes(this.status_id)
  }
}

registerModel(AccountResume)
